import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, RefreshCw, XCircle, CreditCard, AlertTriangle } from "lucide-react";

export default function RefundPolicy() {
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Link to="/">
          <Button variant="ghost" size="sm" className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </Link>

        <div className="bg-card border border-border rounded-lg p-8 shadow-card">
          <h1 className="font-display text-3xl font-bold text-foreground mb-2">Refund & Cancellation Policy</h1>
          <p className="text-muted-foreground mb-8">Last updated: January 2025</p>

          <div className="space-y-6">
            {/* Cancellation Policy */}
            <Card className="bg-background/50 border-border">
              <CardHeader>
                <CardTitle className="font-display text-xl flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-primary" />
                  Cancellation Policy
                </CardTitle>
              </CardHeader>
              <CardContent className="text-muted-foreground space-y-3">
                <p>
                  <strong className="text-foreground">Free Cancellation:</strong> You may cancel or modify your reservation at no charge if done at least <strong className="text-primary">2 hours before</strong> the scheduled meal time.
                </p>
                <p>
                  <strong className="text-foreground">Late Cancellation:</strong> Cancellations made less than 2 hours before the meal time may be subject to a charge of up to 50% of the meal price.
                </p>
                <p>
                  <strong className="text-foreground">No-Show:</strong> If you do not arrive for your reservation without prior cancellation, you may be charged the full meal price.
                </p>
              </CardContent>
            </Card>

            {/* Refund Policy */}
            <Card className="bg-background/50 border-border">
              <CardHeader>
                <CardTitle className="font-display text-xl flex items-center gap-2">
                  <RefreshCw className="w-5 h-5 text-primary" />
                  Refund Policy
                </CardTitle>
              </CardHeader>
              <CardContent className="text-muted-foreground space-y-3">
                <p>
                  <strong className="text-foreground">Eligible Refunds:</strong> If you cancel your reservation within the allowed timeframe (2+ hours before meal time), any prepayment will be fully refunded.
                </p>
                <p>
                  <strong className="text-foreground">Refund Processing Time:</strong>
                </p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Credit Card refunds: 5-10 business days</li>
                  <li>PayPal refunds: 3-5 business days</li>
                  <li>Sinpe Móvil refunds: 1-3 business days</li>
                  <li>Cash payments: Refunded on-site</li>
                </ul>
                <p>
                  <strong className="text-foreground">Partial Refunds:</strong> In cases of partial consumption or service issues, partial refunds may be issued at management's discretion.
                </p>
              </CardContent>
            </Card>

            {/* Service Issues */}
            <Card className="bg-background/50 border-border">
              <CardHeader>
                <CardTitle className="font-display text-xl flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-primary" />
                  Service Issues
                </CardTitle>
              </CardHeader>
              <CardContent className="text-muted-foreground space-y-3">
                <p>
                  If you experience any issues with your meal or service, please notify our staff immediately. We are committed to resolving any problems and ensuring your satisfaction.
                </p>
                <p>
                  <strong className="text-foreground">Quality Guarantee:</strong> If your meal does not meet your expectations due to our error (wrong order, quality issues, dietary requirements not met), we will either replace the meal at no charge or provide a full refund.
                </p>
              </CardContent>
            </Card>

            {/* Payment Information */}
            <Card className="bg-background/50 border-border">
              <CardHeader>
                <CardTitle className="font-display text-xl flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-primary" />
                  Payment Terms
                </CardTitle>
              </CardHeader>
              <CardContent className="text-muted-foreground space-y-3">
                <p>
                  <strong className="text-foreground">Payment Due:</strong> Full payment is due by 8:00 PM the night before your departure date. You may pay at any time after your reservation is confirmed, as early as 1 hour before the meal time.
                </p>
                <p>
                  <strong className="text-foreground">Accepted Payment Methods:</strong>
                </p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Cash (US Dollars or Costa Rican Colones)</li>
                  <li>Credit Cards (Visa, MasterCard)</li>
                  <li>PayPal</li>
                  <li>Sinpe Móvil</li>
                </ul>
                <p>
                  <strong className="text-foreground">Currency:</strong> All prices are listed in US Dollars (USD). Payments in Costa Rican Colones are accepted at the current exchange rate.
                </p>
              </CardContent>
            </Card>

            {/* Contact for Refunds */}
            <div className="bg-primary/10 border border-primary/30 rounded-lg p-6 mt-6">
              <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                Questions About Refunds?
              </h3>
              <p className="text-muted-foreground">
                If you have any questions about our refund or cancellation policy, please contact us:
              </p>
              <div className="mt-3 text-muted-foreground">
                <p>Email: info@amayascorcovado.com</p>
                <p>Or speak with our staff at the restaurant</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
